import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';

import { GenericStatus, useCreateWorkvivoWebhookMutation, Webhook } from 'api';
import { useNotify } from 'hooks';
import { Loaders, ValidatedTextFieldNext } from 'components';
import { WorkVivoSchema } from './Schema';
import { useCreateWorkvivoWebhook } from 'features/admin/updaters/useCreateWebhook';
import { getAppImage } from 'helpers';

interface Props extends Partial<Webhook> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const WorkVivoModal: FC<Props> = ({ isOpen, setIsOpen, ...webhook }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const formRef = useRef<FormikProps<any>>(null);

  const initialValues = {
    workvivo_org_id: webhook.workvivo_org_id,
    api_token: '',
    webhook_status: GenericStatus.Active,
  };

  const queryOptions = useCreateWorkvivoWebhook();
  const { mutate, isLoading } = useCreateWorkvivoWebhookMutation({
    onError: () => setIsOpen(false),
    onSuccess,
    ...queryOptions,
  });

  function onSuccess() {
    setIsOpen(false);
    formRef.current?.resetForm();
    notify.info(t('admin:webhookCreated'));
  }

  function onClickCancel() {
    setIsOpen(false);
    formRef.current?.resetForm();
  }

  function onSubmit(values: typeof initialValues) {
    mutate({ ...values });
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      validationSchema={WorkVivoSchema}
      validateOnBlur={false}
    >
      {({ isValid, setFieldError }) => (
        <Dialog open={isOpen} fullWidth maxWidth="xs">
          <Form>
            <DialogContent>
              <Box marginBottom={5}>
                <Grid container alignItems="center" justifyContent="center" direction="column">
                  <Grid>
                    <Box marginTop={2} marginBottom={2}>
                      <img
                        alt="workvivo_logo"
                        src={getAppImage('wv_logo.svg', 30).src}
                        style={{ height: 50 }}
                      />
                    </Box>
                  </Grid>
                  <Grid xs={10}>
                    <Typography variant="h6">{t('admin:enterCredentials')}</Typography>
                  </Grid>
                  <Grid xs>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      dangerouslySetInnerHTML={{ __html: t('admin:learnMoreIntegrations') }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Grid container direction="column" spacing={5}>
                <Grid xs={12}>
                  <ValidatedTextFieldNext
                    fullWidth
                    label={t('admin:workvivoId')}
                    InputLabelProps={{ shrink: true }}
                    name="workvivo_org_id"
                    onFocus={() => setFieldError('workvivo_org_id', '')}
                  />
                </Grid>

                <Grid xs={12}>
                  <ValidatedTextFieldNext
                    fullWidth
                    label={t('common:authorization')}
                    InputLabelProps={{ shrink: true }}
                    name="api_token"
                    onFocus={() => setFieldError('api_token', '')}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ marginTop: '8px', marginBottom: '32px' }}>
              <Grid container alignItems="center" justifyContent="center" spacing={2}>
                <Grid>
                  <Button fullWidth variant="contained" size="large" onClick={onClickCancel}>
                    {t('common:cancel')}
                  </Button>
                </Grid>

                <Grid>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading || !isValid}
                  >
                    {isLoading ? <Loaders.Standard height={26} /> : t('common:connect')}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default WorkVivoModal;
